import { observer } from 'mobx-react';
import { useEffect, useState, useRef } from 'react';
import colors from 'assets/scss/colors.module.scss';
import Panel from 'app/components/Panel';
import { useStore } from 'app/context';
import { formatBM, formatNum } from 'app/utils';
import { AgtGoogleProfileChangeModel } from 'app/stores/agt.googleprofile.store';
import { init, EChartsOption, ECharts } from 'echarts';
import { Icon } from 'app/components/common/Icon';

interface IAgtGoogleProfileDetailProps {
	agtId: number;
	onClose: () => void;
}

interface ChartDataPoint {
	bm: number;
	bmFormatted: string;
	bew: number;
	neueBew: number | null;
}

export const AgtGoogleProfileDetail = observer((props: IAgtGoogleProfileDetailProps) => {
	const { agtId, onClose } = props;
	const { agtGoogleProfileStore } = useStore();
	const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

	useEffect(() => {
		agtGoogleProfileStore.loadTimeSeriesByAgt(agtId).then((items: AgtGoogleProfileChangeModel[]) => {
			const dataByBm = new Map(items.map((item) => [item.bm, item]));

			// Generate last 12 BMs for the line (bew), neueBew only from 2026
			const latestBm = items.length > 0 ? items[items.length - 1].bm : 202603;
			const bms: number[] = [];
			let year = Math.floor(latestBm / 100);
			let month = latestBm % 100;
			for (let i = 0; i < 12; i++) {
				bms.unshift(year * 100 + month);
				month--;
				if (month < 1) {
					month = 12;
					year--;
				}
			}

			setChartData(
				bms.map((bm: number) => {
					const item = dataByBm.get(bm);
					return {
						bm,
						bmFormatted: formatBM(bm),
						bew: item ? item.bew : 0,
						neueBew: bm >= 202601 ? (item ? item.changeNeueBew : 0) : null,
					};
				}),
			);
		});
	}, [agtGoogleProfileStore, agtId]);

	const chartRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!chartRef.current || chartData.length === 0) return;

		const chart = init(chartRef.current);

		// Prepare data for echarts
		const xAxisData = chartData.map((d) => d.bmFormatted);
		const bewData = chartData.map((d) => d.bew);
		const neueBewData = chartData.map((d) => d.neueBew);

		// Calculate max value for right y-axis domain
		const neueBewMax = Math.max(...neueBewData.filter((v) => v !== null).map((v) => v as number), 0);
		const rightYAxisMax = Math.max(5, neueBewMax);

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				formatter: (params: any) => {
					if (!Array.isArray(params)) return '';
					const month = params[0]?.axisValue || '';
					let result = `<strong>${month}</strong><br/>`;
					params.forEach((param: any) => {
						if (param.value !== null && param.value !== undefined) {
							result += `${param.marker} ${param.seriesName}: ${formatNum(param.value)}<br/>`;
						}
					});
					return result;
				},
			},
			legend: {
				data: ['Bewertungen', 'Neue Bewertungen'],
				bottom: 0,
			},
			grid: {
				top: 20,
				right: 80,
				bottom: 60,
				left: 60,
			},
			xAxis: {
				type: 'category',
				data: xAxisData,
				axisLabel: {
					fontSize: 11,
				},
			},
			yAxis: [
				{
					type: 'value',
					name: 'Bewertungen',
					position: 'left',
					nameLocation: 'middle',
					nameGap: 45,
					nameTextStyle: {
						fontSize: 12,
					},
					axisLabel: {
						formatter: (value: number) => formatNum(value),
					},
					splitLine: {
						lineStyle: {
							type: 'dashed',
						},
					},
				},
				{
					type: 'value',
					name: 'Neue Bewertungen',
					position: 'right',
					nameLocation: 'middle',
					nameGap: 65,
					nameTextStyle: {
						fontSize: 12,
					},
					min: 0,
					max: rightYAxisMax,
					axisLabel: {
						formatter: (value: number) => formatNum(value),
					},
					splitLine: {
						show: false,
					},
				},
			],
			series: [
				{
					name: 'Bewertungen',
					type: 'line',
					data: bewData,
					yAxisIndex: 0,
					itemStyle: {
						color: colors.primary,
					},
					lineStyle: {
						color: colors.primary,
						width: 2,
					},
					connectNulls: true,
					smooth: false,
				},
				{
					name: 'Neue Bewertungen',
					type: 'bar',
					data: neueBewData,
					yAxisIndex: 1,
					barWidth: 40,
					itemStyle: {
						color: colors.turquoise,
					},
					markLine: {
						symbol: 'none',
						data: [
							{
								yAxis: 3,
								lineStyle: {
									color: colors.orange,
									type: [6, 3] as any,
									width: 2,
								},
								label: {
									formatter: 'Ziel: 3',
									position: 'insideEndTop',
									color: colors.orange,
									fontSize: 11,
								},
							},
						],
					},
				},
			],
		};

		chart.setOption(option);

		const handleResize = () => {
			chart.resize();
		};
		window.addEventListener('resize', handleResize);

		return () => {
			chart.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [chartData]);

	return (
		<Panel title="Google Bewertungen der letzten 12 Monate" subtitle={`Agentur: ${agtId}`} onClose={onClose} header={<></>} classModifier="is-googleprofile-detail">
			<div>
				<div className="pad-top-1rem content is-centered">
					<p>
						<strong>Google Bewertungen der letzten 12 Monate</strong>
					</p>
				</div>
				{chartData.length > 0 ? <div ref={chartRef} style={{ width: '99%', height: 300 }} /> : <div className="pad-1rem">Keine Daten vorhanden</div>}

				<div className="pad-1rem margin-top-3rem">
					<div className="note-box content margin-bottom-1rem">
						<p>
							<Icon iconClass="info-circle" />
						</p>
						<div className="note-message">
							<p>
								<strong>Hinweis</strong>
							</p>
							<ul>
								<li>Neue Bewertungen werden erst ab 01.2026 gezählt.</li>
								<li>Monatsziel: 3 Neue Bewertungen</li>
								<li>GP: 3 mal 3 Neue Bewertungen pro Monat in 2026</li>
							</ul>
							<p></p>
						</div>
					</div>
				</div>
			</div>
		</Panel>
	);
});
